// Presentational block renderers (MOD-14 / Blok F).
//
// Pure, dependency-light views for the 7 block types. Shared by BOTH the Puck
// config (edit-canvas render) and the read-only "Önizle" preview, so the two
// stay structurally identical. These are admin-internal previews — structural
// fidelity, not pixel-perfect (the real public render lands in Faz 5 emlak-web).
// `mapEmbed` shows a static placeholder (Maps key is customer-supplied).

import { useTranslation } from 'react-i18next';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { isSafeUrl } from '@shared/utils/safeUrl';
import { sanitizeHtml } from '@shared/utils/sanitize';
import type {
  CardProps,
  CtaProps,
  HeroProps,
  MapEmbedProps,
  RichTextProps,
} from './blockTypes';

/** A link href is only rendered when it passes the same safe-url gate as the BE. */
function safeHref(url: string | undefined | null): string | undefined {
  return url && isSafeUrl(url) ? url : undefined;
}

export function HeroView(props: HeroProps) {
  const img = safeHref(props.imageUrl);
  const cta = safeHref(props.ctaLink);
  return (
    <section
      className="ds-block ds-block--hero"
      style={{
        position: 'relative',
        padding: '48px 32px',
        borderRadius: 12,
        overflow: 'hidden',
        background: img ? undefined : 'var(--ds-surface-2, #f0f2f5)',
        color: img ? '#fff' : 'inherit',
        minHeight: 180,
      }}
    >
      {img ? (
        <img
          src={resolveAssetUrl(img)}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            filter: 'brightness(0.55)',
          }}
        />
      ) : null}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{ margin: 0, fontSize: 28 }}>{props.title || '—'}</h2>
        {props.subtitle ? (
          <p style={{ marginTop: 8, fontSize: 16, opacity: 0.9 }}>{props.subtitle}</p>
        ) : null}
        {props.ctaText && cta ? (
          <a
            href={cta}
            style={{
              display: 'inline-block',
              marginTop: 16,
              padding: '8px 20px',
              borderRadius: 8,
              background: 'var(--ds-brand, #0a7d55)',
              color: '#fff',
              textDecoration: 'none',
            }}
          >
            {props.ctaText}
          </a>
        ) : null}
      </div>
    </section>
  );
}

export function RichTextView(props: RichTextProps) {
  return (
    <div
      className="ds-block ds-block--richtext"
      // Sanitized with the same allow-list as content bodies.
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.html) }}
    />
  );
}

function CardView({ card }: { card: CardProps }) {
  const img = safeHref(card.imageUrl);
  const link = safeHref(card.link);
  const body = (
    <div
      style={{
        border: '1px solid var(--ds-border, #e5e7eb)',
        borderRadius: 10,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {img ? (
        <img
          src={resolveAssetUrl(img)}
          alt={card.title}
          style={{ width: '100%', height: 120, objectFit: 'cover' }}
        />
      ) : null}
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 600 }}>{card.title || '—'}</div>
        {card.text ? (
          <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.8 }}>{card.text}</p>
        ) : null}
      </div>
    </div>
  );
  return link ? (
    <a href={link} style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </a>
  ) : (
    body
  );
}

export function CardGridView({ cards }: { cards: CardProps[] }) {
  const list = Array.isArray(cards) ? cards : [];
  return (
    <div
      className="ds-block ds-block--cardgrid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
      }}
    >
      {list.map((card, i) => (
        <CardView key={i} card={card} />
      ))}
    </div>
  );
}

export function CtaView(props: CtaProps) {
  const link = safeHref(props.buttonLink);
  return (
    <section
      className="ds-block ds-block--cta"
      style={{
        textAlign: 'center',
        padding: '32px 24px',
        borderRadius: 12,
        background: 'var(--ds-surface-2, #f0f2f5)',
      }}
    >
      <h3 style={{ margin: 0 }}>{props.title || '—'}</h3>
      {props.text ? <p style={{ marginTop: 8, opacity: 0.85 }}>{props.text}</p> : null}
      {props.buttonText && link ? (
        <a
          href={link}
          style={{
            display: 'inline-block',
            marginTop: 16,
            padding: '10px 24px',
            borderRadius: 8,
            background: 'var(--ds-brand, #0a7d55)',
            color: '#fff',
            textDecoration: 'none',
          }}
        >
          {props.buttonText}
        </a>
      ) : null}
    </section>
  );
}

/** Structural placeholder for an embedded reference (form/slider). */
export function EmbedPlaceholder({ kind, label }: { kind: string; label: string }) {
  return (
    <div
      className="ds-block ds-block--embed"
      style={{
        padding: 20,
        borderRadius: 10,
        border: '1px dashed var(--ds-border, #cbd5e1)',
        background: 'var(--ds-surface-2, #f8fafc)',
        color: 'var(--ds-text-secondary, #64748b)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{kind}</div>
      <div style={{ fontWeight: 600, marginTop: 4 }}>{label}</div>
    </div>
  );
}

export function MapEmbedView(props: MapEmbedProps) {
  const { t } = useTranslation('pages');
  return (
    <div
      className="ds-block ds-block--map"
      style={{
        padding: 24,
        borderRadius: 10,
        border: '1px dashed var(--ds-border, #cbd5e1)',
        background:
          'repeating-linear-gradient(45deg, var(--ds-surface-2, #eef2f7), var(--ds-surface-2, #eef2f7) 10px, var(--ds-surface, #f7f9fc) 10px, var(--ds-surface, #f7f9fc) 20px)',
        textAlign: 'center',
        color: 'var(--ds-text-secondary, #64748b)',
      }}
    >
      <div style={{ fontWeight: 600 }}>{props.title || t('builder.blocks.mapEmbed.label')}</div>
      <div style={{ fontSize: 12, marginTop: 4 }}>
        {/* `lng` is a reserved i18next option (target language) — pass as `lngValue`. */}
        {t('builder.blocks.mapEmbed.coords', { lat: props.lat, lngValue: props.lng })}
      </div>
      <div style={{ fontSize: 12, marginTop: 2 }}>{t('builder.blocks.mapEmbed.placeholder')}</div>
    </div>
  );
}
